// src/services/adminSignupService.js
import { repo } from '../dbservice/repo';
import { sendAccessRequestAck } from './brevoEmailService';
import { AdminSignupModel } from '../models/AdminSignupModel';



// ─── Email Availability Check ─────────────────────────────────────────────────

/**
 * Checks if an email already exists in adminAccessRequests or users.
 * Both are CENTRAL_COLLECTIONS → always hits Firebase via repo routing.
 *
 * @param {string} email
 * @returns {Promise<{ exists: boolean, location?: string }>}
 */
export const checkEmailAvailability = async (email) => {
  if (!email?.trim()) return { exists: false };

  try {
    const normalizedEmail = email.trim().toLowerCase();

    const [adminResults, userResults] = await Promise.all([
      repo.fetchData(
        'adminAccessRequests',
        [{ field: 'email', value: normalizedEmail }],
        [],
        1,
      ),
      repo.fetchData(
        'users',
        [{ field: 'email', value: normalizedEmail }],
        [],
        1,
      ),
    ]);

    if (adminResults.length > 0) return { exists: true, location: 'admin requests' };
    if (userResults.length > 0)  return { exists: true, location: 'users' };

    return { exists: false };
  } catch (err) {
    console.error('[AdminSignupService] Email availability check failed:', err);
    return { exists: false }; // Fail open — never block the user on network hiccup
  }
};

// ─── Form Validation ──────────────────────────────────────────────────────────

/**
 * Validates all fields in the admin signup form.
 * Runs the model's pure structural validation first, then the async
 * duplicate-email check (which needs a DB round-trip and stays here).
 *
 * @param {Object} formData
 * @returns {Promise<{ valid: boolean, error?: string }>}
 */
export const validateAdminSignupForm = async (formData) => {
  /* ── 1. Structural validation via model ──────────────────────────── */
  const instance = new AdminSignupModel(
    { ...formData, status: 'pending', requestedRole: 'admin' },
    { isCreation: true },
  );

  if (!instance.isValid()) {
    // Provide a readable error using the original message shape
    const requiredFields = [
      'name', 'email', 'company', 'companyId',
      'countryCode', 'stateCode', 'city', 'contactPersonMobile',
    ];
    for (const field of requiredFields) {
      if (!formData[field]?.trim?.()) {
        return {
          valid: false,
          error: `Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field`,
        };
      }
    }

    if (!AdminSignupModel.EMAIL_REGEX.test(formData.email || '')) {
      return { valid: false, error: 'Please enter a valid email address' };
    }

    if (!AdminSignupModel.COMPANY_CODE_REGEX.test((formData.companyId || '').toUpperCase())) {
      return {
        valid: false,
        error: 'Company ID must be alphanumeric with optional dashes or underscores',
      };
    }

    if (!AdminSignupModel.MOBILE_REGEX.test((formData.contactPersonMobile || '').trim())) {
      return {
        valid: false,
        error: 'Contact person mobile must be exactly 10 digits',
      };
    }

    return { valid: false, error: 'Invalid form data. Please review and try again.' };
  }

  /* ── 2. Duplicate email check against DB ─────────────────────────── */
  const emailCheck = await checkEmailAvailability(formData.email);
  if (emailCheck.exists) {
    return {
      valid: false,
      error: `Email already exists in ${emailCheck.location}. Please use a different email.`,
    };
  }

  return { valid: true };
};

// ─── Payload Builder ──────────────────────────────────────────────────────────

/**
 * Transforms raw formData into the clean document payload for persistence.
 * Now delegates shaping to AdminSignupModel so the shape lives in one place.
 *
 * @param {Object} formData
 * @returns {Object}
 */
const buildAdminRequestPayload = (formData) => {
  const instance = new AdminSignupModel(formData, { isCreation: true });

  const payload = instance.toPayload();

  // Drop model-injected nulls for the optional alias so the doc stays lean
  if (!payload.location && payload.city) payload.location = payload.city;

  return payload;
};

// ─── Main Submission Entry Point ──────────────────────────────────────────────

/**
 * Full admin signup submission pipeline:
 *   1. Validate all form fields
 *   2. Build clean payload (via model)
 *   3. Persist via repo.upsertData()
 *   4. Fire acknowledgment email 
 *
 * @param {Object} formData - raw form state from the component
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const submitAdminSignupRequest = async (formData) => {
  /* Step 1 — Validate */
  const validation = await validateAdminSignupForm(formData);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  /* Step 2 — Build payload (model validates + shapes) */
  const payload = buildAdminRequestPayload(formData);

  // Final safety gate — model must validate the shaped payload too
  const guard = new AdminSignupModel(
    { ...payload, createdAt: new Date().toISOString() },
    { isCreation: true },
  );
  if (!guard.isValid()) {
    return {
      success: false,
      error: 'Submission failed validation. Please review your details.',
    };
  }

  /* Step 3 — Persist via generic repo method */
  const requestId = `admin_req_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  try {
    await repo.upsertData('adminAccessRequests', requestId, {
      ...guard.toPayload(),
      createdAt: new Date().toISOString(),
    });
  } catch (err) {
    console.error('[AdminSignupService] Persistence failed:', err);
    return {
      success: false,
      error: err.message || 'Submission failed. Please try again.',
    };
  }

  /* Step 4 — Acknowledgment email (fire and forget) */
  sendAccessRequestAck({
    name:  payload.name,
    email: payload.email,
    role:  'admin',
  }).catch((err) =>
    console.warn('[AdminSignupService] Acknowledgment email failed silently:', err),
  );

  return { success: true };
};